import { JarvisWebHttpClient } from '@jarvis/web-http';
const axios = require('axios');
const mime = require('mime-types');

export const photoUploadEndpoint = '/api/v3/user/upload';
const photoUploadStatusEndpoint = '/api/v3/user/uploadStatus';

const projectsEndpoint = '/api/v3/user/projects';

const userEndpoint = '/api/v3/user';

const cartEndpoint = '/api/v3/user/cart';

const checkoutInfoEndpoint = '/api/v2/user/checkout'; // v2 is old checkout info
const checkoutPSFEndpoint = '/api/v3/user/checkout'; // v3 is the new PSF version of checkout info

const ordersEndpoint = '/api/v2/user/orders';

const internalAddToCartEndpoint = '/api/v3/internal/user/addToCart';
const internalProjectsEndpoint = '/api/v3/internal/user/projects';

export interface PhotoUploadResponse {
  photoId: string;
  photoUrl: string;
  filename: string;
  ready: boolean;
  remaining: number;
}

export class PhotobookApi {
  jarvisWebHttpInstance: JarvisWebHttpClient;

  constructor(jarvisWebHttpInstance: JarvisWebHttpClient) {
    this.jarvisWebHttpInstance = jarvisWebHttpInstance;
  }

  public async deleteUser() {
    console.log('Deleting user', userEndpoint);
    return this.jarvisWebHttpInstance.delete({
      url: userEndpoint,
    });
  }

  public async uploadPhoto(photoUrl: string) {
    console.log('Uploading photo', photoUrl);

    const formData = new FormData();

    // Obtain the photo
    try {
      // Photo as blob
      console.log(`${photoUrl} Getting Photo as Blob`);
      const { data: blob } = await axios.get(photoUrl, { responseType: 'blob' });
      console.log(`${photoUrl} Blob type`, blob.type);
      const extension = mime.extension(blob.type);
      console.log(`${photoUrl} Using file extension`, extension);
      const file = new File([blob], `uploading-${Date.now()}.${extension}`, {
        type: blob.type,
      });
      formData.append('photo', file);
    } catch (error) {
      console.error(`${photoUrl} Failed to obtain photo`, error);
      throw error;
    }

    // Post it to backend
    const uploadResponse = await this.jarvisWebHttpInstance.post({
      url: photoUploadEndpoint,
      data: formData,
      headers: { 'content-type': 'multipart/form-data' },
    });

    console.log(`${photoUrl} Upload Response`, uploadResponse);

    // wait until the photo is ready
    const photoId = uploadResponse.data.photoId;
    let remaining = uploadResponse.data.remaining;

    let status = '';
    const maxIterations = 5;
    let i = 0;
    do {
      await new Promise((r) => setTimeout(r, remaining));
      console.log(`${photoId} Waiting (${remaining}) to check photo upload status`);

      const statusResponse = await this.jarvisWebHttpInstance.get({
        url: `${photoUploadStatusEndpoint}?photoId=${photoId}&attemptNumber=${i}`,
      });
      status = statusResponse.data.status;
      remaining = statusResponse.data.remaining;
      console.log(`${photoId} Get Upload Status Response (iteration ${i})`, statusResponse);
    } while (i++ < maxIterations && status == 'Pending');

    if (i == maxIterations) {
      console.error(`Photo Upload status still pending after ${i} attempts`);
    }
    return uploadResponse;
  }

  public async waitUntilUploadFinished(mediaclipPhotoId: string, remaining: number) {
    console.log('Checking photo upload status', mediaclipPhotoId);

    console.log(`${mediaclipPhotoId} Upload Response check again in ${remaining}ms`);

    // wait until the photo is ready
    let status = '';
    const maxIterations = 5;
    let i = 0;
    do {
      await new Promise((r) => setTimeout(r, remaining));
      console.log(`${mediaclipPhotoId} Waiting (${remaining}) to check photo upload status`);

      const statusResponse = await this.jarvisWebHttpInstance.get({
        url: `${photoUploadStatusEndpoint}?photoId=${mediaclipPhotoId}&attemptNumber=${i}`,
      });
      status = statusResponse.data.status;
      remaining = statusResponse.data.remaining;
      console.log(`${mediaclipPhotoId} Get Upload Status Response (iteration ${i})`, statusResponse);
    } while (i++ < maxIterations && status == 'Pending');

    if (i == maxIterations) {
      console.error(`Photo Upload status still pending after ${i} attempts`);
    }
    return mediaclipPhotoId;
  }

  public async createProject(data: Record<string, unknown>) {
    console.log('Creating a new project with data.', data);
    return this.jarvisWebHttpInstance.post({
      url: projectsEndpoint,
      data: data,
    });
  }

  public async getProjects() {
    console.log('Retrieving projects');
    return this.jarvisWebHttpInstance.get({
      url: projectsEndpoint,
    });
  }

  public async getProject(id: string) {
    const endpoint = projectsEndpoint + '/' + id;
    console.log('Retrieving project', endpoint);
    return this.jarvisWebHttpInstance.get({
      url: endpoint,
    });
  }

  public async deleteProject(id: string) {
    const endpoint = projectsEndpoint + '/' + id;
    console.log('Deleting project', endpoint);
    return this.jarvisWebHttpInstance.delete({
      url: endpoint,
    });
  }

  public async getCart() {
    console.log('Retrieving cart');

    return this.jarvisWebHttpInstance.get({
      url: cartEndpoint,
    });
  }

  public async createOrder(validStatuses: number[]) {
    console.log('Creating order');
    return this.jarvisWebHttpInstance.post({
      url: ordersEndpoint,
      validateStatus: (status) => validStatuses.includes(status),
    });
  }

  public async getOrders() {
    console.log('Retrieving orders');
    return this.jarvisWebHttpInstance.get({
      url: ordersEndpoint,
    });
  }

  public async getCheckoutInfo() {
    console.log('Retrieving photobook checkoutInfo');

    return this.jarvisWebHttpInstance.get({
      url: checkoutInfoEndpoint,
    });
  }

  public async getPsfCheckoutInfo() {
    console.log('Retrieving PSF checkoutInfo');

    return this.jarvisWebHttpInstance.get({
      url: checkoutPSFEndpoint,
    });
  }

  public async internalGetProjectPhotos(projectId: string) {
    const endpoint = `${internalProjectsEndpoint}/${projectId}/photos`;
    console.warn(`Using internal api ${endpoint}`);
    return this.jarvisWebHttpInstance.get({
      url: endpoint,
    });
  }

  public async internalAddToCart() {
    console.warn(`Using internal api ${internalAddToCartEndpoint}`);
    return this.jarvisWebHttpInstance.post({
      url: internalAddToCartEndpoint,
    });
  }
}

export default {
  PhotobookApi,
};
